// src/config.js

import nvlrLogo from "./assets/tokens/nvlr.png";
import nuveLogo from "./assets/tokens/nuve.png";
import sbrLogo from "./assets/tokens/sbr.png";
import fluffLogo from "./assets/tokens/fluff.png";
import squartzLogo from "./assets/tokens/squartz.png";
import smalachiteLogo from "./assets/tokens/smalachite.png";
import ssapphireLogo from "./assets/tokens/ssapphire.png";
import samethystLogo from "./assets/tokens/samethyst.png";
import stopazLogo from "./assets/tokens/stopaz.png";
import salmazLogo from "./assets/tokens/salmaz.png";
import sfuelLogo from "./assets/tokens/sfuel.png";
import hotLogo from "./assets/tokens/hot-icon.png";
import jamboLogo from "./assets/tokens/jambo.webp";
import { resolvePublicApiOrigin } from "./projectMeta";

// Primary browser RPC endpoint with CORS.
export const NEAR_RPC_URL = import.meta.env.VITE_NEAR_RPC_URL || "https://rpc.mainnet.fastnear.com";
const DEFAULT_NEAR_RPC_FALLBACKS = [
  "https://c1.rpc.fastnear.com",
  "https://c2.rpc.fastnear.com",
  "https://relmn.aurora.dev",
  "https://nearrpc.aurora.dev",
  "https://archival-rpc.mainnet.near.org",
];
export const NEAR_RPC_URLS = Array.from(new Set([
  NEAR_RPC_URL,
  ...String(import.meta.env.VITE_NEAR_RPC_FALLBACKS || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean),
  ...DEFAULT_NEAR_RPC_FALLBACKS,
].map((value) => String(value || "").replace(/\/+$/, "")).filter(Boolean)));
export const FASTNEAR_API_URL = import.meta.env.VITE_FASTNEAR_API_URL || "https://api.fastnear.com";
export const IPFS_GATEWAY_PRIMARY =
  import.meta.env.VITE_IPFS_GATEWAY_PRIMARY ||
  import.meta.env.VITE_PINATA_GATEWAY ||
  "https://ipfs.io/ipfs/";
export const IPFS_GATEWAY_FALLBACKS = String(
  import.meta.env.VITE_IPFS_GATEWAY_FALLBACKS ||
    "https://w3s.link/ipfs/,https://gateway.pinata.cloud/ipfs/,https://ipfs.near.social/ipfs/"
)
  .split(",")
  .map((value) => String(value || "").trim())
  .filter(Boolean);
// Backup options:
// export const NEAR_RPC_URL = import.meta.env.VITE_NEAR_RPC_URL || "https://rpc.mainnet.fastnear.com";

const CONFIG = {
  NEAR_RPC_URL,
  NETWORK: "mainnet",
};

export default CONFIG;

export const API_ORIGIN = String(
  import.meta.env.VITE_API_ORIGIN ||
    resolvePublicApiOrigin(typeof window !== "undefined" ? window.location : null)
).replace(/\/+$/, "");

const RAW_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? "/api/" : `${API_ORIGIN}/api_app/api/`);
export const API_BASE_URL = RAW_API_BASE_URL.endsWith("/")
  ? RAW_API_BASE_URL
  : `${RAW_API_BASE_URL}/`;
export const apiUrl = (path) => `${API_BASE_URL}${String(path || "").replace(/^\/+/, "")}`;
export const API_HOLDKROL_URL = API_BASE_URL + "holdkrol";
export const API_LEADERBOARD_URL = API_BASE_URL + "leaderboard";
export const API_HOUSE_BREAKDOWN_URL = API_BASE_URL + "house-breakdown";
export const API_HOUSE_HOLDERS_URL   = API_BASE_URL + "house-holders";
export const API_HOUSE_HOLDER_ITEMS_URL = API_BASE_URL + "house-holder-items";
export const API_OWNER_KROLIK_URL = API_BASE_URL + "owner-krolik";
export const API_TOKEN_HOLDERS_URL = API_BASE_URL + "tokens/holders";
export const API_MERKLE_COLLECTIONS = API_BASE_URL + "merkle/collections";
export const API_MERKLE_TITLES = API_BASE_URL + "merkle/titles";
export const API_MERKLE_ROOTS = API_BASE_URL + "merkle/roots";
export const API_MERKLE_PROOF = API_BASE_URL + "merkle/proof";
export const API_CRAFT_FT_CONTRACTS = API_BASE_URL + "craft/ft-contracts";
export const API_PRICES_URL = API_BASE_URL + "prices";

export const BOOST_CACHE_URL  = `${API_ORIGIN}/api/boost_cache.json`;
export const SEASONS_URL = API_BASE_URL + "game-seasons";
export const BONUS_TABLE_URL  = `${API_ORIGIN}/api/bonus-table`;

export const FT_URL = "/cache/raw_ft.json";
export const NFT_URL = "/cache/raw_nft.json";
export const SEASON_TXT_URL = "/cache/season.txt";

export const DEFAULT_ALLOCATION   = 90000000;
export const ROWS_OPTIONS         = [10, 20, 50, 100];
export const BLACK_HOLE_ACCOUNT_ID = "blackhole.nuvelor.near";
export const NUVE_CONTRACT_ID = "nuve.nuvelor.near";
export const NVLR_CONTRACT_ID = "nvlr.nuvelor.near";
export const SCORE_CONTRACT_ID = import.meta.env.VITE_SCORE_CONTRACT_ID || "score.nuvelor.near";
export const STAKING_CONTRACT_ID = import.meta.env.VITE_STAKING_CONTRACT_ID || "staking.nuvelor.near";
export const NUVE_DECIMALS = 4;
export const NVLR_DECIMALS = 8;
export const BLACK_HOLE_FORMULA_VERSION = "blackhole_score_v1";

export const DEFAULT_TOKENS = [
  { symbol: "NVLR", contractId: "nvlr.nuvelor.near", name: "Nuvelor", decimals: 8, icon: nvlrLogo },
  { symbol: "NUVE", contractId: NUVE_CONTRACT_ID, name: "Nuve", decimals: NUVE_DECIMALS, icon: nuveLogo },
  { symbol: "JAMBO", contractId: "jambo-1679.meme-cooking.near", name: "JAMBO", decimals: 18, icon: jamboLogo },
  { symbol: "SBR", contractId: "sbr.tkn.near", name: "Siberia Token", decimals: 8, icon: sbrLogo },
  { symbol: "FLUFF", contractId: "fluff.tkn.near", name: "Fluffy", decimals: 3, icon: fluffLogo },
  { symbol: "sFuel", contractId: "sfuel.tkn.near", name: "Siberian Fuel", decimals: 0, icon: sfuelLogo },
  { symbol: "sQuartz", contractId: "squartz.tkn.near", name: "splinter Quartz", decimals: 0, icon: squartzLogo },
  { symbol: "sMalachite", contractId: "smalachite.tkn.near", name: "splinter Malachite", decimals: 0, icon: smalachiteLogo },
  { symbol: "sSapphire", contractId: "ssapphire.tkn.near", name: "splinter Sapphire", decimals: 0, icon: ssapphireLogo },
  { symbol: "sAmethyst", contractId: "samethyst.tkn.near", name: "splinter Amethyst", decimals: 0, icon: samethystLogo },
  { symbol: "sTopaz", contractId: "stopaz.tkn.near", name: "splinter Topaz", decimals: 0, icon: stopazLogo },
  { symbol: "sAlmaz", contractId: "salmaz.tkn.near", name: "splinter Almaz", decimals: 0, icon: salmazLogo },
];

export const SUGGESTED_TOKENS = [
  { symbol: "HOT", contractId: "game.hot.tg", name: "HOT", decimals: 6, icon: hotLogo },
];

export const DB_TOKEN_CONTRACTS = [
  { contractId: "fluff.tkn.near", standard: "ft", symbol: "FLUFF" },
  { contractId: "krolik.mintbase1.near", standard: "nft", symbol: "KROLIK" },
  { contractId: "nft.nuvelor.near", standard: "nft", symbol: "NUVELOR" },
  { contractId: "salmaz.tkn.near", standard: "ft", symbol: "sAlmaz" },
  { contractId: "samethyst.tkn.near", standard: "ft", symbol: "sAmethyst" },
  { contractId: "sbr.tkn.near", standard: "ft", symbol: "SBR" },
  { contractId: "sfuel.tkn.near", standard: "ft", symbol: "sFuel" },
  { contractId: "siberiashop.mintbase1.near", standard: "nft", symbol: "NUVELOR" },
  { contractId: "smalachite.tkn.near", standard: "ft", symbol: "sMalachite" },
  { contractId: "squartz.tkn.near", standard: "ft", symbol: "sQuartz" },
  { contractId: "ssapphire.tkn.near", standard: "ft", symbol: "sSapphire" },
  { contractId: "stopaz.tkn.near", standard: "ft", symbol: "sTopaz" },
];

export const NFT_CONTRACTS = {
  "Nuvelor": "nft.nuvelor.near",
  "NUVELOR Rabbits": "krolik.mintbase1.near",
  "NUVELOR": "siberiashop.mintbase1.near",
  "NUVELOR Rabbit": "rabbits.nfts.tg",
};

// Accounts allowed to opt in to production diagnostics from Profile.
export const DEBUG_ADMINS = [
  "kvp_vs.tg",
];

// Incident-scoped diagnostic access only. This list must never grant product/admin privileges.
export const DEBUG_SUPPORT_ACCOUNTS = [
  "drsemenenko.tg",
];

// Production-preview features visible only to explicitly allowed test accounts.
export const PREVIEW_FEATURE_ADMINS = [
  "kvp_vs.tg"
];
